import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { usePhones, useCreatePhone, useUpdatePhone, useDeletePhone } from '../api'
import { Modal } from '../components/Modal'
import { PhoneForm } from '../components/PhoneForm'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Pagination } from '../components/Pagination'
import { PhoneTable } from '../components/PhoneTable'
import { sortPhones, type SortState } from '../lib/sortPhones'
import { PhoneMobileCard } from '../components/PhoneMobileCard'
import type { Phone } from '../types'

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; phone: Phone }
  | { type: 'delete'; phone: Phone }

type TabKey = 'all' | 'stuck' | 'orphan' | 'attached'

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'stuck', label: 'Висят на админе' },
  { key: 'orphan', label: 'Свободные' },
  { key: 'attached', label: 'У операторов' },
]

const PAGE_SIZE = 20

function bucket(phone: Phone): TabKey {
  if (phone.ownerKind === 'admin') return 'stuck'
  if (phone.ownerKind === 'orphan' || phone.userId === null) return 'orphan'
  return 'attached'
}

function filterByTab(phones: Phone[], tab: TabKey): Phone[] {
  if (tab === 'all') return phones
  return phones.filter(p => bucket(p) === tab)
}

function searchFilter(phones: Phone[], q: string): Phone[] {
  if (!q.trim()) return phones
  const needle = q.trim().toLowerCase()
  return phones.filter(p =>
    p.name.toLowerCase().includes(needle) || p.sim.toLowerCase().includes(needle),
  )
}

function readTab(p: URLSearchParams): TabKey {
  const t = p.get('tab') as TabKey | null
  return t && TABS.some(x => x.key === t) ? t : 'all'
}

function readPage(p: URLSearchParams): number {
  const n = Number(p.get('page'))
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1
}

export function DashboardPage() {
  const { data: phones = [], isLoading, error } = usePhones()
  const createPhone = useCreatePhone()
  const updatePhone = useUpdatePhone()
  const deletePhone = useDeletePhone()
  const [modal, setModal] = useState<ModalState>({ type: 'none' })
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = readTab(searchParams)
  const page = readPage(searchParams)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' })

  const online = phones.filter(p => p.status !== 'offline').length

  const counts = useMemo(() => {
    const c: Record<TabKey, number> = { all: phones.length, stuck: 0, orphan: 0, attached: 0 }
    for (const p of phones) c[bucket(p)] += 1
    return c
  }, [phones])

  const visible = useMemo(() => {
    const filtered = searchFilter(filterByTab(phones, tab), search)
    return sortPhones(filtered, sort)
  }, [phones, tab, search, sort])

  const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  // Tab/search may shrink the list — clamp page back to last available.
  const effectivePage = Math.min(page, totalPages)
  const pageStart = (effectivePage - 1) * PAGE_SIZE
  const pageRows = visible.slice(pageStart, pageStart + PAGE_SIZE)

  const setTab = (next: TabKey) => {
    // Reset page to 1 when tab changes — operator filter switches the
    // visible set entirely, so a stale ?page=N would land on empty.
    const sp = new URLSearchParams(searchParams)
    sp.set('tab', next)
    sp.delete('page')
    setSearchParams(sp, { replace: true })
  }

  const setPage = (next: number) => {
    const sp = new URLSearchParams(searchParams)
    if (next === 1) sp.delete('page')
    else sp.set('page', String(next))
    setSearchParams(sp, { replace: true })
  }

  const closeModal = () => {
    setModal({ type: 'none' })
    createPhone.reset()
    updatePhone.reset()
    deletePhone.reset()
  }

  const handleCreate = (data: { name: string; sim_number: string }) => {
    createPhone.mutate(data, { onSuccess: closeModal })
  }
  const handleEdit = (data: { name: string; sim_number: string }) => {
    if (modal.type !== 'edit') return
    updatePhone.mutate({ id: modal.phone.id, ...data }, { onSuccess: closeModal })
  }
  const handleDelete = () => {
    if (modal.type !== 'delete') return
    deletePhone.mutate(modal.phone.id, { onSuccess: closeModal })
  }
  const handleToggleActive = (phone: Phone) => {
    updatePhone.mutate({ id: phone.id, is_active: !phone.isActive })
  }
  const handleDetachFromAdmin = (phone: Phone) => {
    updatePhone.mutate({ id: phone.id, user_id: null })
  }

  // Stable callback bag — without useMemo every render hands new function
  // identities to every PhoneRow / PhoneMobileCard, defeating React.memo
  // if a child gets memoized later.
  const actions = useMemo(
    () => ({
      onEdit: (p: Phone) => setModal({ type: 'edit', phone: p }),
      onDelete: (p: Phone) => setModal({ type: 'delete', phone: p }),
      onToggleActive: handleToggleActive,
      onDetachFromAdmin: handleDetachFromAdmin,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updatePhone],
  )

  return (
    <div className="max-w-screen-2xl mx-auto px-4 py-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Phones</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {online}/{phones.length} online
          </p>
        </div>
        <button
          onClick={() => setModal({ type: 'create' })}
          className="text-sm font-medium px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          + Add Phone
        </button>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap items-center gap-2 mb-3 border-b border-gray-200 dark:border-gray-700">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`text-base font-medium px-3 py-2 -mb-px border-b-2 transition-colors ${
              tab === t.key
                ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {t.label} <span className="text-xs text-gray-400">({counts[t.key]})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="mb-3">
        <input
          type="search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск по имени или SIM..."
          aria-label="Поиск трубок"
          className="w-full sm:max-w-sm text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:outline-none focus:border-blue-500"
        />
      </div>

      {isLoading && (
        <p className="text-gray-400 dark:text-gray-500 text-center py-8">Loading...</p>
      )}

      {error && (
        <p className="text-red-500 text-center py-8">{error.message}</p>
      )}

      {!isLoading && !error && phones.length === 0 && (
        <p className="text-gray-400 dark:text-gray-500 text-center py-8">No phones yet</p>
      )}

      {!isLoading && !error && phones.length > 0 && (
        <>
          {totalPages > 1 && (
            <div className="mb-4">
              <Pagination
                currentPage={effectivePage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}

          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <PhoneTable
              phones={pageRows}
              sort={sort}
              onSortChange={setSort}
              emptyText="Ничего не найдено"
              {...actions}
            />
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-2">
            {pageRows.length === 0 ? (
              <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                Ничего не найдено
              </p>
            ) : (
              pageRows.map(p => <PhoneMobileCard key={p.id} phone={p} {...actions} />)
            )}
          </div>

          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                currentPage={effectivePage}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}

      {/* Create Phone Modal */}
      <Modal open={modal.type === 'create'} onClose={closeModal} title="Add Phone">
        <PhoneForm
          onSubmit={handleCreate}
          onCancel={closeModal}
          isPending={createPhone.isPending}
          error={createPhone.error}
        />
      </Modal>

      {/* Edit Phone Modal */}
      <Modal open={modal.type === 'edit'} onClose={closeModal} title="Edit Phone">
        {modal.type === 'edit' && (
          <PhoneForm
            initialValues={{ name: modal.phone.name, sim: modal.phone.sim }}
            onSubmit={handleEdit}
            onCancel={closeModal}
            isPending={updatePhone.isPending}
            error={updatePhone.error}
          />
        )}
      </Modal>

      {/* Delete Confirmation */}
      {modal.type === 'delete' && (
        <ConfirmDialog
          open
          onClose={closeModal}
          onConfirm={handleDelete}
          title="Delete Phone"
          message={`Delete "${modal.phone.name}"? This cannot be undone.`}
          isPending={deletePhone.isPending}
        />
      )}
    </div>
  )
}
